/**
 * المرحلة 1.35 — «دفتر الإقامة»: one stay's nursing booklet, page by page as
 * the centre's paper has it (photos 10–16):
 *
 *   خريطة العناية     date, time, temperature, pulse, sugar, follow-up, signature;
 *                     a row is a `vitals` record, so it lands on Observations too
 *   ملاحظات يومية     «اليوم الأول»، «اليوم الثاني»… from the admission date
 *   أوامر الطبيب      the four headings and the dated treatment; the doctor writes,
 *                     the nurse reads
 *   الأدوية           drug, time and signature
 *   المستهلكات        an item the price list marks «يضيفه التمريض» and a count;
 *                     the unit cost and the total are the list's, read on the server
 *   زيارة الطبيب      date, hour and signature
 *
 * Every page is paper whatever the theme, and prints as it is. The server
 * (routes/stays.js) decides who writes which page; `data.can` only keeps the
 * forms away from those it would refuse.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, ErrorState, Field, Icon, Input, Modal, Select, Spinner, Tabs, Textarea, TimeInput, DateInput } from '../../components/common';
import { staysApi } from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { activeCountry, formatDate, formatDateTime, formatMoney, formatTime } from '../../domain/locale';
import { ageLabel } from '../../domain/childFile';
import {
  BOOKLET_PAGES,
  MED_ROUTES,
  blankCare,
  blankOrder,
  careBody,
  careReady,
  consumablesOf,
  dayKey,
  dayLabel,
  latestOrders,
  mayVoid,
  notePages,
  orderPayload,
  orderReady,
  stayDay,
} from '../../domain/stays';
import { Line, PrintButton, SheetHead } from '../childFile/Paper';

const PAGE_ICONS = {
  care: 'activity',
  days: 'notebook-pen',
  orders: 'stethoscope',
  medications: 'pill',
  consumables: 'package',
  visits: 'user-check',
};

const tz = () => activeCountry().timezone || 'Africa/Cairo';
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** A write to the booklet: the fresh stay back, a toast either way. */
function useWrite(onChange) {
  const { success, error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);
  const run = async (call, done) => {
    setBusy(true);
    try {
      const fresh = await call();
      onChange(fresh);
      if (done) success(done);
      return true;
    } catch (err) {
      toastError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

/** The page's head lines every booklet page repeats: the child, the diagnosis, the admission and the days. */
function StayLines({ data }) {
  const { t } = useI18n();
  const { stay, patient } = data;
  return (
    <div className="cf-lines cf-lines--two">
      <Line label={t('stay.care.name')} wide>
        {patient?.fullName}
      </Line>
      <Line label={t('stay.care.today')}>{formatDate(new Date())}</Line>
      <Line label={t('stay.care.admittedAt')}>{formatDateTime(stay.admittedAt)}</Line>
      <Line label={t('stay.care.diagnosis')} wide>
        {stay.diagnosis}
      </Line>
      <Line label={t('stay.care.days')}>{String(stay.days)}</Line>
      <Line label={t('stay.banner.unit')}>{stay.unitLabel}</Line>
    </div>
  );
}

/* ── خريطة العناية ─────────────────────────────────────────────────────── */

function CarePage({ data, onChange }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const [form, setForm] = useState(blankCare);
  const [busy, write] = useWrite(onChange);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = async () => {
    if (await write(() => staysApi.record(data.stay.id, careBody(form)), t('stay.care.added'))) setForm(blankCare());
  };
  const rows = data.booklet.care;
  return (
    <div className="space-y-3">
      {data.can.care && (
        <div className="sb-form" data-testid="stay-care-form">
          {['temperature', 'pulse', 'glucose', 'spo2', 'bp'].map((key) => (
            <Field key={key} label={t(`stay.care.${key}`)}>
              <Input
                inputMode={key === 'bp' ? 'numeric' : 'decimal'}
                dir="ltr"
                value={form[key]}
                onChange={set(key)}
                placeholder={key === 'bp' ? '90/60' : undefined}
                data-testid={`stay-care-${key}`}
              />
            </Field>
          ))}
          <Field label={t('stay.care.followUp')} className="sb-form__wide">
            <Input value={form.followUp} onChange={set('followUp')} data-testid="stay-care-followUp" />
          </Field>
          <Button icon="plus" loading={busy} disabled={!careReady(form)} onClick={save} data-testid="stay-care-add">
            {t('stay.care.add')}
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="stay-print" />
      </div>
      <article ref={ref} className="cf-paper" data-testid="stay-care">
        <SheetHead title={t('stay.care.title')} />
        <StayLines data={data} />
        <div className="cf-scroll">
          <table className="cf-table cf-table--rows">
            <thead>
              <tr>
                <th>{t('stay.care.date')}</th>
                <th>{t('stay.care.time')}</th>
                <th>{t('stay.care.temperature')}</th>
                <th>{t('stay.care.pulse')}</th>
                <th>{t('stay.care.glucose')}</th>
                <th>{t('stay.care.followUp')}</th>
                <th>{t('stay.care.signature')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="cf-table__empty">
                    {t('stay.care.empty')}
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} data-testid="stay-care-row">
                  <td data-label={t('stay.care.date')}>{formatDate(r.at)}</td>
                  <td data-label={t('stay.care.time')}>{formatTime(r.at)}</td>
                  <td dir="ltr" data-label={t('stay.care.temperature')}>{r.payload.temperature ?? ''}</td>
                  <td dir="ltr" data-label={t('stay.care.pulse')}>{r.payload.pulse ?? ''}</td>
                  <td dir="ltr" data-label={t('stay.care.glucose')}>{r.payload.glucose ?? ''}</td>
                  <td data-label={t('stay.care.followUp')}>{[r.payload.spo2 ? `SpO₂ ${r.payload.spo2}%` : null, r.payload.bp ? `${t('stay.care.bp')} ${r.payload.bp}` : null, r.text].filter(Boolean).join(' · ')}</td>
                  <td data-label={t('stay.care.signature')}>{r.by || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}

/* ── ملاحظات يومية ─────────────────────────────────────────────────────── */

function DaysPage({ data, onChange }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const [text, setText] = useState('');
  const [busy, write] = useWrite(onChange);
  // Today's page on top, by the form; the paper's order is one print away.
  const pages = useMemo(() => notePages(data.stay, data.booklet.days, { timeZone: tz() }).reverse(), [data.stay, data.booklet.days]);
  const today = stayDay(data.stay.admittedAt, new Date(), tz());
  const save = async () => {
    if (await write(() => staysApi.record(data.stay.id, { kind: 'note', text: text.trim() }), t('stay.days.added'))) setText('');
  };
  return (
    <div className="space-y-3">
      {data.can.care && (
        <div className="cf-nform" data-testid="stay-note-form">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('stay.days.placeholder')} data-testid="stay-note-text" />
          <Button icon="plus" loading={busy} disabled={!text.trim()} onClick={save} data-testid="stay-note-add">
            {t('stay.days.add', { day: dayLabel(today, t) })}
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="stay-print" />
      </div>
      <div ref={ref} className="sb-days" data-testid="stay-days">
        {pages.map((p) => (
          <article key={p.day} className="cf-paper cf-paper--notes sb-day" data-testid="stay-day">
            <header className="sb-day__head">
              <h3 className="sb-day__title">{dayLabel(p.day, t)}</h3>
              <span className="sb-day__date">{p.date ? formatDate(`${p.date}T12:00:00Z`) : ''}</span>
            </header>
            <table className="cf-ruled">
              <tbody>
                {p.notes.map((n) => (
                  <tr key={n.id}>
                    <td className="sb-day__time">{formatTime(n.at)}</td>
                    <td>
                      {n.text}
                      {n.by && <span className="cf-ruled__by"> — {n.by}</span>}
                    </td>
                  </tr>
                ))}
                {Array.from({ length: Math.max(0, 3 - p.notes.length) }, (_, i) => (
                  <tr key={`blank-${i}`} className="cf-ruled__blank">
                    <td> </td>
                    <td> </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {p.notes.length === 0 && <p className="sb-day__empty">{t('stay.days.empty')}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}

/* ── أوامر الطبيب ──────────────────────────────────────────────────────── */

function OrderForm({ data, onChange }) {
  const { t } = useI18n();
  const today = dayKey(new Date(), tz());
  const [form, setForm] = useState(() => blankOrder(today));
  const [busy, write] = useWrite(onChange);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setLine = (i, key) => (e) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [key]: e.target.value } : l)) }));
  const save = async () => {
    if (await write(() => staysApi.record(data.stay.id, orderPayload(form)), t('stay.orders.saved'))) setForm(blankOrder(today));
  };
  return (
    <section className="sb-order" data-testid="stay-order-form">
      <div className="sb-order__heads">
        {['vitals', 'feeding', 'fluids', 'oxygen'].map((key) => (
          <Field key={key} label={t(`stay.orders.${key}`)}>
            <Input value={form[key]} onChange={set(key)} data-testid={`stay-order-${key}`} />
          </Field>
        ))}
      </div>
      {form.lines.map((l, i) => (
        <div key={i} className="sb-order__line">
          <Field label={t('stay.orders.date')}>
            <DateInput value={l.date} onChange={setLine(i, 'date')} clearable={false} />
          </Field>
          <Field label={t('stay.orders.treatment')} className="sb-order__treatment">
            <Input value={l.treatment} onChange={setLine(i, 'treatment')} data-testid="stay-order-treatment" />
          </Field>
        </div>
      ))}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" icon="plus" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { date: today, treatment: '' }] }))}>
          {t('stay.orders.addLine')}
        </Button>
        <Button icon="save" loading={busy} disabled={!orderReady(form)} onClick={save} data-testid="stay-order-save">
          {t('stay.orders.save')}
        </Button>
      </div>
    </section>
  );
}

function OrdersPage({ data, onChange }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const { head, lines } = useMemo(() => latestOrders(data.booklet.orders), [data.booklet.orders]);
  const signer = data.booklet.orders[0] || null;
  return (
    <div className="space-y-3">
      {data.can.order ? <OrderForm data={data} onChange={onChange} /> : <p className="sb-note">{t('stay.orders.readOnly')}</p>}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="stay-print" />
      </div>
      <article ref={ref} className="cf-paper" data-testid="stay-orders">
        <SheetHead title={t('stay.orders.title')} subtitle={data.patient?.fullName} />
        <div className="sb-heads">
          {['vitals', 'feeding', 'fluids', 'oxygen'].map((key) => (
            <div key={key} className="sb-heads__box">
              <span className="sb-heads__label">{t(`stay.orders.${key}`)}</span>
              <span className="sb-heads__value">{head[key] || ' '}</span>
            </div>
          ))}
        </div>
        <div className="cf-scroll">
          <table className="cf-table cf-table--rows">
            <thead>
              <tr>
                <th>{t('stay.orders.date')}</th>
                <th>{t('stay.orders.treatment')}</th>
                <th>{t('stay.orders.signature')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={3} className="cf-table__empty">
                    {t('stay.orders.empty')}
                  </td>
                </tr>
              )}
              {lines.map((l, i) => (
                <tr key={`${l.at}-${i}`} data-testid="stay-order-row">
                  <td data-label={t('stay.orders.date')}>{l.date ? formatDate(`${l.date}T12:00:00Z`) : formatDate(l.at)}</td>
                  <td data-label={t('stay.orders.treatment')}>{l.treatment}</td>
                  <td data-label={t('stay.orders.signature')}>{l.by || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="cf-sign">
          <Line label={t('stay.orders.signature')}>{signer?.by || ''}</Line>
          <Line label={t('stay.orders.date')}>{signer ? formatDate(signer.at) : ''}</Line>
        </div>
      </article>
    </div>
  );
}

/* ── الأدوية ───────────────────────────────────────────────────────────── */

function MedicationsPage({ data, onChange }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const blank = () => ({ name: '', dose: '', route: 'oral', time: nowTime() });
  const [form, setForm] = useState(blank);
  const [busy, write] = useWrite(onChange);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = async () => {
    const payload = { name: form.name.trim(), dose: form.dose.trim() || null, route: t(`stay.meds.route.${form.route}`), time: form.time || null };
    if (await write(() => staysApi.record(data.stay.id, { kind: 'medication', payload }), t('stay.meds.added'))) setForm(blank());
  };
  const rows = data.booklet.medications;
  const half = Math.ceil(rows.length / 2);
  const columns = rows.length > 1 ? [rows.slice(0, half), rows.slice(half)] : [rows, []];
  return (
    <div className="space-y-3">
      {data.can.care && (
        <div className="sb-form" data-testid="stay-med-form">
          <Field label={t('stay.meds.name')} className="sb-form__wide">
            <Input value={form.name} onChange={set('name')} data-testid="stay-med-name" />
          </Field>
          <Field label={t('stay.meds.dose')}>
            <Input value={form.dose} onChange={set('dose')} data-testid="stay-med-dose" />
          </Field>
          <Field label={t('stay.meds.routeLabel')}>
            <Select value={form.route} onChange={set('route')}>
              {MED_ROUTES.map((r) => (
                <option key={r} value={r}>
                  {t(`stay.meds.route.${r}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('stay.meds.time')}>
            <TimeInput value={form.time} onChange={set('time')} data-testid="stay-med-time" />
          </Field>
          <Button icon="plus" loading={busy} disabled={!form.name.trim()} onClick={save} data-testid="stay-med-add">
            {t('stay.meds.add')}
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="stay-print" />
      </div>
      <article ref={ref} className="cf-paper" data-testid="stay-meds">
        <SheetHead title={t('stay.meds.title')} subtitle={data.patient?.fullName} />
        {rows.length === 0 && <p className="cf-table__empty">{t('stay.meds.empty')}</p>}
        <div className="sb-twin">
          {columns.map((col, c) => (
            <table key={c} className="cf-table cf-table--rows">
              <thead>
                <tr>
                  <th>{t('stay.meds.name')}</th>
                  <th>{t('stay.meds.time')}</th>
                  <th>{t('stay.meds.signature')}</th>
                </tr>
              </thead>
              <tbody>
                {col.map((r) => (
                  <tr key={r.id} data-testid="stay-med-row">
                    <td data-label={t('stay.meds.name')}>
                      <b>{r.payload.name}</b>
                      {[r.payload.dose, r.payload.route].filter(Boolean).length > 0 && <span className="sb-sub"> {[r.payload.dose, r.payload.route].filter(Boolean).join(' · ')}</span>}
                    </td>
                    <td data-label={t('stay.meds.time')}>
                      <span dir="ltr">{r.payload.time || formatTime(r.at)}</span>
                      <span className="sb-sub"> {formatDate(r.at)}</span>
                    </td>
                    <td data-label={t('stay.meds.signature')}>{r.by || ''}</td>
                  </tr>
                ))}
                {Array.from({ length: Math.max(0, 4 - col.length) }, (_, i) => (
                  <tr key={`blank-${i}`} className="sb-blank">
                    <td> </td>
                    <td> </td>
                    <td> </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </article>
    </div>
  );
}

/* ── المستهلكات ────────────────────────────────────────────────────────── */

function VoidDialog({ charge, onClose, onChange }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [busy, write] = useWrite(onChange);
  const save = async () => {
    if (await write(() => staysApi.voidCharge(charge.id, reason.trim()).then(() => staysApi.get(charge.stayId)), t('stay.cons.voided'))) onClose();
  };
  return (
    <Modal open onClose={onClose} title={t('stay.cons.void')} icon="x-circle" size="sm">
      <p className="mb-3 text-sm">{charge.label}</p>
      <Field label={t('stay.cons.voidReason')}>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="stay-void-reason" autoFocus />
      </Field>
      <div className="mt-4 flex justify-end">
        <Button variant="danger" icon="x-circle" loading={busy} disabled={!reason.trim()} onClick={save} data-testid="stay-void-save">
          {t('stay.cons.void')}
        </Button>
      </div>
    </Modal>
  );
}

/** A line's state as the account sees it: waiting for review, reviewed, or voided. */
export function ChargeState({ charge }) {
  const { t } = useI18n();
  if (charge.status === 'void') return <Badge className="sb-state sb-state--void">{t('stay.cons.voidedLabel')}</Badge>;
  if (charge.status === 'reviewed')
    return (
      <Badge className="sb-state sb-state--ok" icon="check">
        {t('stay.cons.reviewed')}
      </Badge>
    );
  return (
    <Badge className="sb-state sb-state--wait" icon="clock">
      {t('stay.cons.waiting')}
    </Badge>
  );
}

function ConsumablesPage({ data, options, onChange }) {
  const { t } = useI18n();
  const { session } = useAuth();
  const ref = useRef(null);
  const items = useMemo(() => options?.nursingItems || [], [options]);
  const [itemId, setItemId] = useState('');
  const [count, setCount] = useState('1');
  const [voiding, setVoiding] = useState(null);
  const [busy, write] = useWrite(onChange);
  const item = items.find((i) => String(i.id) === String(itemId));
  const qty = Number(String(count).replace(/[٠-٩]/g, (c) => '٠١٢٣٤٥٦٧٨٩'.indexOf(c))) || 0;
  const save = async () => {
    if (await write(() => staysApi.charge(data.stay.id, { priceItemId: Number(itemId), quantity: qty }), t('stay.cons.added'))) {
      setItemId('');
      setCount('1');
    }
  };
  const rows = consumablesOf(data.charges, items);
  const total = rows.reduce((s, c) => s + Number(c.amount), 0);
  return (
    <div className="space-y-3">
      {options && !options.priceList && <p className="sb-note">{t('stay.cons.noList')}</p>}
      {options && options.priceList && items.length === 0 && <p className="sb-note">{t('stay.cons.noItems')}</p>}
      {data.can.charge && data.stay.status === 'open' && items.length > 0 && (
        <div className="sb-form" data-testid="stay-cons-form">
          <Field label={t('stay.cons.pick')} className="sb-form__wide">
            <Select value={itemId} onChange={(e) => setItemId(e.target.value)} data-testid="stay-cons-item">
              <option value="">—</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.path.join(' › ')} — {formatMoney(i.price)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('stay.cons.count')}>
            <Input inputMode="decimal" dir="ltr" value={count} onChange={(e) => setCount(e.target.value)} data-testid="stay-cons-count" />
          </Field>
          <div className="sb-form__price" aria-live="polite">
            {item && qty > 0 ? `${formatMoney(item.price)} × ${qty} = ${formatMoney(Math.round(item.price * qty * 100) / 100)}` : ' '}
          </div>
          <Button icon="plus" loading={busy} disabled={!item || qty <= 0} onClick={save} data-testid="stay-cons-add">
            {t('stay.cons.add')}
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="stay-print" />
      </div>
      <article ref={ref} className="cf-paper" data-testid="stay-cons">
        <SheetHead title={t('stay.cons.title')} subtitle={data.patient?.fullName} />
        <div className="cf-scroll">
          <table className="cf-table cf-table--rows">
            <thead>
              <tr>
                <th>{t('stay.cons.date')}</th>
                <th>{t('stay.cons.count')}</th>
                <th>{t('stay.cons.item')}</th>
                <th>{t('stay.cons.unitCost')}</th>
                <th>{t('stay.cons.total')}</th>
                <th>{t('stay.cons.signature')}</th>
                <th className="cf-noprint" aria-label={t('stay.cons.state')} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="cf-table__empty">
                    {t('stay.cons.empty')}
                  </td>
                </tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} data-testid="stay-cons-row">
                  <td data-label={t('stay.cons.date')}>{formatDate(c.occurredAt)}</td>
                  <td dir="ltr" data-label={t('stay.cons.count')}>{c.quantity}</td>
                  <td data-label={t('stay.cons.item')}>{c.label}</td>
                  <td className="sb-money" data-label={t('stay.cons.unitCost')}>{formatMoney(c.unitPrice)}</td>
                  <td className="sb-money" data-label={t('stay.cons.total')}>{formatMoney(c.amount)}</td>
                  <td data-label={t('stay.cons.signature')}>{c.recordedName || ''}</td>
                  <td className="cf-noprint sb-actions">
                    <ChargeState charge={c} />
                    {mayVoid(c, { userId: session?.id, isAccounts: data.can.chargeAny }) && (
                      <button type="button" className="cf-table__remove" onClick={() => setVoiding(c)} title={t('stay.cons.void')} aria-label={t('stay.cons.void')}>
                        <Icon name="x-circle" size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4}>{t('stay.cons.total')}</td>
                  <td className="sb-money">{formatMoney(total)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </article>
      {voiding && <VoidDialog charge={voiding} onClose={() => setVoiding(null)} onChange={onChange} />}
    </div>
  );
}

/* ── زيارة الطبيب ──────────────────────────────────────────────────────── */

export function VisitsPage({ data, onChange }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const [note, setNote] = useState('');
  const [busy, write] = useWrite(onChange);
  const save = async () => {
    if (await write(() => staysApi.record(data.stay.id, { kind: 'visit', text: note.trim() || undefined }), t('stay.visits.added'))) setNote('');
  };
  const rows = data.booklet.visits;
  return (
    <div className="space-y-3">
      {data.can.order && (
        <div className="cf-nform" data-testid="stay-visit-form">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('stay.visits.note')} data-testid="stay-visit-note" />
          <Button icon="user-check" loading={busy} onClick={save} data-testid="stay-visit-add">
            {t('stay.visits.add')}
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="stay-print" />
      </div>
      <article ref={ref} className="cf-paper" data-testid="stay-visits">
        <SheetHead title={t('stay.visits.title')} subtitle={data.patient?.fullName} />
        <div className="cf-scroll">
          <table className="cf-table cf-table--rows">
            <thead>
              <tr>
                <th>{t('stay.visits.date')}</th>
                <th>{t('stay.visits.time')}</th>
                <th>{t('stay.visits.signature')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="cf-table__empty">
                    {t('stay.visits.empty')}
                  </td>
                </tr>
              )}
              {rows.map((v) => (
                <tr key={v.id} data-testid="stay-visit-row">
                  <td data-label={t('stay.visits.date')}>{formatDate(v.at)}</td>
                  <td data-label={t('stay.visits.time')}>{formatTime(v.at)}</td>
                  <td data-label={t('stay.visits.signature')}>
                    {v.by || ''}
                    {v.text && <span className="sb-sub"> — {v.text}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}

/* ── the booklet ───────────────────────────────────────────────────────── */

/** The strip over the pages: the child, the unit, the day, the doctor, the state. */
export function StayBanner({ data }) {
  const { t } = useI18n();
  const { stay, patient } = data;
  const day = stay.status === 'open' ? stayDay(stay.admittedAt, new Date(), tz()) : null;
  return (
    <div className={`sb-banner sb-banner--${stay.status}`} data-testid="stay-banner">
      <span className="sb-banner__icon" aria-hidden="true">
        <Icon name="bed-double" size={24} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="sb-banner__name">{patient?.fullName}</p>
        <p className="sb-banner__meta">
          {patient?.dateOfBirth && <span>{t('stay.banner.age', { age: ageLabel(patient.dateOfBirth, t) })}</span>}
          {stay.unitLabel && <span>{stay.unitLabel}</span>}
          {stay.attendingDoctorName && (
            <span>
              {t('stay.banner.doctor')}: {stay.attendingDoctorName}
            </span>
          )}
          <span>{t('stay.banner.admitted', { date: formatDateTime(stay.admittedAt) })}</span>
        </p>
        {stay.diagnosis && <p className="sb-banner__diagnosis">{stay.diagnosis}</p>}
      </div>
      <div className="sb-banner__state">
        {day && <span className="sb-chip sb-chip--day">{dayLabel(day, t)}</span>}
        {stay.status === 'discharged' && <span className="sb-chip">{t(`stay.discharge.kind.${stay.dischargeKind}`)}</span>}
        {stay.clearedAt && stay.status === 'open' && (
          <span className="sb-chip sb-chip--ok">
            <Icon name="badge-check" size={14} /> {t('stay.banner.cleared')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * @param {{ stayId: number, defaultPage?: string, onChanged?: (data) => void }} props
 */
export function StayBooklet({ stayId, defaultPage = 'care', onChanged }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(BOOKLET_PAGES.includes(defaultPage) ? defaultPage : 'care');
  const load = () =>
    staysApi
      .get(stayId)
      .then((d) => {
        setData(d);
        setError(null);
        onChanged?.(d);
      })
      .catch(setError);
  useEffect(() => {
    load();
    staysApi
      .options()
      .then(setOptions)
      .catch(() => setOptions({ nursingItems: [], priceList: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stayId]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return <Spinner label={t('stay.loading')} variant="inline" />;

  const change = (fresh) => {
    if (fresh && fresh.stay) {
      setData(fresh);
      onChanged?.(fresh);
    } else load();
  };
  const counts = {
    care: data.booklet.care.length,
    days: data.booklet.days.reduce((s, d) => s + d.notes.length, 0),
    orders: data.booklet.orders.length,
    medications: data.booklet.medications.length,
    consumables: consumablesOf(data.charges, options?.nursingItems || []).length,
    visits: data.booklet.visits.length,
  };
  return (
    <div className="sb" data-testid="stay-booklet">
      <StayBanner data={data} />
      <Tabs tabs={BOOKLET_PAGES.map((id) => ({ id, label: t(`stay.page.${id}`), icon: PAGE_ICONS[id], count: counts[id] || undefined }))} active={page} onChange={setPage} />
      {page === 'care' && <CarePage data={data} onChange={change} />}
      {page === 'days' && <DaysPage data={data} onChange={change} />}
      {page === 'orders' && <OrdersPage data={data} onChange={change} />}
      {page === 'medications' && <MedicationsPage data={data} onChange={change} />}
      {page === 'consumables' && <ConsumablesPage data={data} options={options} onChange={change} />}
      {page === 'visits' && <VisitsPage data={data} onChange={change} />}
    </div>
  );
}

export default StayBooklet;
